/**
 * The feeds alert rules can be written against.
 *
 * Each source declares its filterable fields, how to identify a record (so an opportunity alerts
 * once, not on every poll) and how to describe one in a notification. Adding a feed to the alert
 * system means adding an entry here — the engine, the hook and the panel need no changes.
 */

import { AlertFieldDef, AlertSourceDef } from './alertRules';
import { computeMarketSignals, marketSignalsKey, MarketSignals } from './alertMarketSignals';
import { FLOW_FIELDS } from './rtlScannerFlowRows';

function str(v: unknown): string {
  return v == null ? '' : String(v);
}

function num(v: unknown): string {
  const n = typeof v === 'number' ? v : parseFloat(String(v ?? ''));
  return Number.isFinite(n) ? n.toLocaleString() : '—';
}

/** Fields common to every scanner tile (Elite Pulse imbalances and Smart Flow alike). */
const TILE_FIELDS: AlertFieldDef[] = [
  { key: 't', label: 'Ticker', type: 'text' },
  { key: 'side', label: 'Call / Put', type: 'enum', options: ['C', 'P'] },
  { key: 'strike', label: 'Strike', type: 'number' },
  { key: 'exp', label: 'Expiry', type: 'text' },
  { key: 'dte', label: 'DTE', type: 'number' },
  { key: 'prem', label: 'Premium', type: 'number' },
  { key: 'rv', label: 'Relative volume', type: 'number' },
  { key: 'flow', label: 'Tape side', type: 'enum', options: ['BUY', 'SELL', 'UNK'] },
  { key: 'fl', label: 'Direction', type: 'enum', options: ['Buy Call', 'Buy Put', 'Sell Call', 'Sell Put', 'Unknown'] },
  { key: 'color', label: 'Bias', type: 'enum', options: ['buy', 'sell', 'unk'] },
  { key: 'mc', label: 'Moneyness', type: 'enum', options: ['ITM', 'ATM', 'OTM', 'NA'] },
  { key: 'money', label: 'Moneyness label', type: 'text' },
  { key: 'horizon_tml', label: 'Horizon', type: 'enum', options: ['[L]', '[M]', '[T]'] },
];

function describeTile(r: Record<string, unknown>): string {
  const bits = [
    str(r.t),
    r.strike == null ? '' : String(r.strike),
    str(r.side) === 'P' ? 'Put' : str(r.side) === 'C' ? 'Call' : '',
    str(r.exp),
  ].filter(Boolean);
  const tail = [str(r.fl), r.prem == null ? '' : `${num(r.prem)} premium`].filter(Boolean).join(' · ');
  // Never return bare punctuation: a notification with no content is worse than none.
  const head = bits.join(' ') || 'Opportunity';
  return tail ? `${head} — ${tail}` : head;
}

/** Tile-shaped records (Elite Pulse, Smart Flow) identify a contract directly. */
function tileHistorical(r: Record<string, unknown>) {
  const underlying = str(r.t).toUpperCase();
  const strike = typeof r.strike === 'number' ? r.strike : parseFloat(str(r.strike));
  if (!underlying || !Number.isFinite(strike)) return null;
  return {
    underlying,
    strike,
    expiry: str(r.exp) || undefined,
    optionType: (str(r.side) === 'P' ? 'PUT' : 'CALL') as 'PUT' | 'CALL',
  };
}

export const ELITE_PULSE_SOURCE: AlertSourceDef = {
  id: 'elite-pulse',
  label: 'Elite Pulse opportunities',
  fields: TILE_FIELDS,
  recordKey: (r) => str(r.id),
  describe: describeTile,
  historicalParams: tileHistorical,
};

/**
 * Rules written on the retired Top Premium Board (Smart Flow). The id is kept so saved rules keep
 * evaluating: Options Tape feeds this source the rows it shows, in the board's tile shape, and
 * its alert drawer opens these rules under this label.
 */
export const SMART_FLOW_SOURCE: AlertSourceDef = {
  id: 'smart-flow',
  label: 'Options Tape presets',
  fields: [
    ...TILE_FIELDS,
    ...FLOW_FIELDS.filter((f) => !TILE_FIELDS.some((t) => t.key === f.key)).map((f) => ({
      key: `raw.${f.key}`,
      label: f.label,
      type: f.type === 'number' ? ('number' as const) : f.type === 'enum' ? ('enum' as const) : ('text' as const),
      options: f.options,
    })),
  ],
  recordKey: (r) => str(r.id),
  describe: describeTile,
  historicalParams: tileHistorical,
};

export const FLOW_TAPE_SOURCE: AlertSourceDef = {
  id: 'flow-tape',
  label: 'Flow tape prints',
  fields: [
    { key: 'ticker', label: 'Ticker', type: 'text' },
    { key: 'optionType', label: 'Call / Put', type: 'enum', options: ['C', 'P'] },
    { key: 'strike', label: 'Strike', type: 'number' },
    { key: 'expiry', label: 'Expiry', type: 'text' },
    { key: 'dte', label: 'DTE', type: 'number' },
    { key: 'premium', label: 'Premium', type: 'number' },
    { key: 'size', label: 'Size', type: 'number' },
    { key: 'volume', label: 'Volume', type: 'number' },
    { key: 'price', label: 'Price', type: 'number' },
    { key: 'side', label: 'Tape side', type: 'enum', options: ['Ask', 'Bid', 'Mid'] },
    { key: 'condition', label: 'Condition', type: 'text' },
    { key: 'exchange', label: 'Exchange', type: 'text' },
  ],
  recordKey: (r) => str(r.id),
  describe: (r) => {
    const head = [str(r.ticker), r.strike == null ? '' : String(r.strike),
      str(r.optionType) === 'P' ? 'Put' : 'Call', str(r.expiry)].filter(Boolean).join(' ') || 'Print';
    const tail = [str(r.side), r.premium == null ? '' : `${num(r.premium)} premium`].filter(Boolean).join(' · ');
    return tail ? `${head} — ${tail}` : head;
  },
  historicalParams: (r) => {
    const underlying = str(r.ticker).toUpperCase();
    const strike = typeof r.strike === 'number' ? r.strike : parseFloat(str(r.strike));
    if (!underlying || !Number.isFinite(strike)) return null;
    return {
      underlying,
      strike,
      expiry: str(r.expiry) || undefined,
      optionType: (str(r.optionType) === 'P' ? 'PUT' : 'CALL') as 'PUT' | 'CALL',
    };
  },
};

/**
 * The stable fetch loop. Its rows are the scanner's own record shape, which uses spaced,
 * capitalised column names — quoted here exactly as they arrive so rules read against reality.
 */
export const STABLE_FETCH_SOURCE: AlertSourceDef = {
  id: 'stable-fetch',
  label: 'Stable fetch (30s loop)',
  fields: [
    { key: 'Underlying', label: 'Underlying', type: 'text' },
    { key: 'Strike', label: 'Strike', type: 'number' },
    { key: 'Option Type', label: 'Call / Put', type: 'enum', options: ['C', 'P'] },
    { key: 'Expiry Date', label: 'Expiry', type: 'text' },
    { key: 'Days left', label: 'Days left', type: 'number' },
    { key: 'Volume', label: 'Volume', type: 'number' },
    { key: 'Open Interest', label: 'Open interest', type: 'number' },
    { key: 'Total Premium', label: 'Total premium', type: 'number' },
    { key: 'Total Volume', label: 'Total volume', type: 'number' },
    { key: 'Bid Volume', label: 'Bid volume', type: 'number' },
    { key: 'Ask Volume', label: 'Ask volume', type: 'number' },
    { key: 'Volume Delta', label: 'Volume delta', type: 'number' },
    { key: 'Delta', label: 'Delta', type: 'number' },
    { key: 'Gamma', label: 'Gamma', type: 'number' },
    { key: 'Vega', label: 'Vega', type: 'number' },
    { key: 'Theta', label: 'Theta', type: 'number' },
    { key: 'Implied Volatility', label: 'Implied volatility', type: 'number' },
    { key: 'Vol/OI', label: 'Vol / OI', type: 'number' },
    { key: 'Moneyness', label: 'Moneyness', type: 'enum', options: ['ITM', 'ATM', 'OTM'] },
    { key: 'Direction', label: 'Direction', type: 'text' },
    { key: 'Sentiment', label: 'Sentiment', type: 'text' },
    { key: 'Underlying Price', label: 'Underlying price', type: 'number' },
    { key: 'Trade Count', label: 'Trade count', type: 'number' },
    // Columns the legacy presets compared against.
    { key: 'Delta Premium', label: 'Delta premium', type: 'number' },
    { key: 'Delta Accumulated Premium', label: 'Delta accumulated premium', type: 'number' },
    { key: 'Delta (Ask-Bid)', label: 'Delta (ask − bid)', type: 'number' },
    { key: 'Delta accumelated', label: 'Delta accumulated', type: 'number' },
    { key: 'Haltz', label: 'Haltz', type: 'number' },
    { key: 'Band', label: 'Band', type: 'number' },
    { key: 'Zahn', label: 'Zahn', type: 'number' },
  ],
  recordKey: (r) =>
    `${str(r.Underlying)}|${str(r.Strike)}|${str(r['Option Type'])}|${str(r['Expiry Date'])}|${str(r.Volume)}`,
  describe: (r) => {
    const head = [str(r.Underlying), r.Strike == null ? '' : String(r.Strike),
      str(r['Option Type']) === 'P' ? 'Put' : 'Call', str(r['Expiry Date'])].filter(Boolean).join(' ') || 'Contract';
    const tail = [
      r.Volume == null ? '' : `vol ${num(r.Volume)}`,
      str(r.Sentiment || r.Direction),
    ].filter(Boolean).join(' · ');
    return tail ? `${head} — ${tail}` : head;
  },
  historicalParams: (r) => {
    const underlying = str(r.Underlying || r.underlyingSymbol).toUpperCase();
    const strike = typeof r.Strike === 'number' ? r.Strike : parseFloat(str(r.Strike));
    if (!underlying || !Number.isFinite(strike)) return null;
    return {
      underlying,
      strike,
      expiry: str(r['Expiry Date']) || undefined,
      optionType: (str(r['Option Type']) === 'P' ? 'PUT' : 'CALL') as 'PUT' | 'CALL',
    };
  },
};

/**
 * Dataset-level signals from the stable fetch: most-active option, volume peaks, sentiment areas,
 * the SPX summary, gamma peaks and per-underlying sentiment. These were the legacy presets that
 * could never fire, because their "field" was analysis output rather than a column on a row.
 */
export const MARKET_SIGNALS_SOURCE: AlertSourceDef = {
  id: 'market-signals',
  label: 'Market signals',
  fields: [
    { key: 'market_summary_change', label: 'SPX summary', type: 'enum', options: ['bullish', 'bearish'] },
    { key: 'spx_today_sentiment', label: 'SPX today sentiment', type: 'enum', options: ['bullish', 'bearish'] },
    { key: 'most_active_option', label: 'Most active option', type: 'text' },
    { key: 'most_active_direction', label: 'Most active direction', type: 'enum', options: ['bought', 'sold', 'neutral'] },
    { key: 'most_active_volume', label: 'Most active volume', type: 'number' },
    { key: 'volume_peak', label: 'Volume peak', type: 'text' },
    { key: 'volume_peak_strike', label: 'Volume peak strike', type: 'number' },
    { key: 'volume_peak_volume', label: 'Volume peak volume', type: 'number' },
    { key: 'sentiment_area', label: 'Sentiment area', type: 'enum', options: ['bullish', 'bearish'] },
    { key: 'spot_gamma_max', label: 'Spot gamma max', type: 'number' },
    { key: 'spot_gamma_max_strike', label: 'Spot gamma max strike', type: 'number' },
    { key: 'premium_max', label: 'Premium max', type: 'number' },
    { key: 'premium_max_symbol', label: 'Premium max symbol', type: 'text' },
    { key: 'premium_min', label: 'Premium min', type: 'number' },
    { key: 'premium_min_symbol', label: 'Premium min symbol', type: 'text' },
    { key: 'delta_max', label: 'Delta max', type: 'number' },
    { key: 'delta_max_symbol', label: 'Delta max symbol', type: 'text' },
    { key: 'delta_min', label: 'Delta min', type: 'number' },
    { key: 'delta_min_symbol', label: 'Delta min symbol', type: 'text' },
    { key: 'underlying_sentiment', label: 'Underlying sentiment', type: 'text' },
    { key: 'sample_size', label: 'Contracts analysed', type: 'number' },
  ],
  recordKey: (r) => marketSignalsKey(r as MarketSignals),
  describe: (r) => {
    const bits: string[] = [];
    if (r.market_summary_change) bits.push(`SPX ${str(r.market_summary_change)}`);
    if (r.sentiment_area) bits.push(`${str(r.sentiment_area)} sentiment area`);
    if (r.most_active_option) bits.push(`most active ${str(r.most_active_option)} (${str(r.most_active_direction)})`);
    if (r.volume_peak) bits.push(`volume peak ${str(r.volume_peak)}`);
    if (r.premium_max_symbol) bits.push(`largest premium ${str(r.premium_max_symbol)}`);
    if (r.underlying_sentiment) bits.push(str(r.underlying_sentiment).slice(0, 80));
    return bits.length ? bits.join(' · ') : `Market signals updated (${str(r.sample_size)} contracts)`;
  },
};

export { computeMarketSignals };

export const ALERT_SOURCES: AlertSourceDef[] = [
  MARKET_SIGNALS_SOURCE,
  ELITE_PULSE_SOURCE,
  SMART_FLOW_SOURCE,
  FLOW_TAPE_SOURCE,
  STABLE_FETCH_SOURCE,
];

export function sourceById(id: string): AlertSourceDef | undefined {
  return ALERT_SOURCES.find((s) => s.id === id);
}

/**
 * Smart Flow rules address tape fields as `raw.<field>`; flatten one level so the engine, which
 * reads plain keys, can evaluate them without knowing about nesting.
 */
export function flattenRawRecord(record: Record<string, unknown>): Record<string, unknown> {
  const raw = record.raw;
  if (!raw || typeof raw !== 'object') return record;
  const out: Record<string, unknown> = { ...record };
  for (const [k, v] of Object.entries(raw as Record<string, unknown>)) {
    out[`raw.${k}`] = v;
  }
  return out;
}
